import type {
    DatasetColumnDataType,
    DatasetColumnRole,
    DatasetType,
    Filter,
    MetricAggregationFunction,
    SortConfiguration,
    TimeGranularity,
} from "@vesta/vos-types/modules/workspaceLevel/domain/Dataset";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export type RawDataColumnMetadata = {
    id: Uuid;
    // This is the index of the column in the dataset.
    columnIndex: number;
    displayName: string;
    dataType: DatasetColumnDataType;
    role: DatasetColumnRole;
};

export type RawData = {
    columnMetadata: Array<RawDataColumnMetadata>;
    dataPoints: Array<
        Array<{
            value: string | number | null;
        }>
    >;
    /**
     * When present, the heat-map transformer emits one row per key (in this order) and treats
     * missing `(row, column)` pairs as **0**. Emotorad zone heat maps populate this from OU
     * catalog so zones without backend rows still appear.
     */
    heatMapFixedRowKeys?: ReadonlyArray<string>;
    /**
     * When present, the heat-map transformer uses this exact column order (one column per key).
     * Missing `(row, column)` pairs are **0** when {@link heatMapFixedRowKeys} / widget fixed rows
     * are in effect. Emotorad "Conversion Volume Heatmap" uses this for a full calendar month
     * (D1 … Dlast) aligned to the month of the dashboard range end.
     */
    heatMapFixedColumnKeys?: ReadonlyArray<string>;
};

// APIs/DataResolvers to Support
//     pointInTime dataset -> pointInTime data
//     pointInTime dataset -> periodic data
//     pointInTime dataset -> non-time-series data
//     periodic dataset -> periodic data
//     periodic dataset -> non-time-series data
//     non-time-series dataset -> non-time-series data

/** Used by fetchAnalyticsData to pick endpoint when dataset is periodic. */
export enum AnalyticsQueryVariant {
    periodicAsPeriodic = "0e7745da-f388-48c7-934b-1454d4fd8fc2",
    periodicAsNonTimeSeries = "9815ef3f-cdb9-4ae6-a0ec-524f3b73a56d",
    pointInTimeAsNonTimeSeries = "47e347c2-f347-403f-8e30-f610f1e89306",
}

// eg: get total spend on ads per day to show in a line graph
type BaseDataResolverProps = {
    datasetId: Uuid;
    filters: Filter;
    dimensions: Array<{
        id: Uuid;
    }>;
    sort: SortConfiguration;
    // inclusive
    from: Iso8601DateTime;
    // exclusive.. I think?
    to: Iso8601DateTime;
    // the timezone to send data back in
    timeZone: Uuid;
    /** If set, used to choose API endpoint. Omitted from request body. */
    queryVariant?: AnalyticsQueryVariant;
};

export type PointInTimeAsPointInTimeDataResolverProps = BaseDataResolverProps & {
    datasetType: DatasetType.pointInTime;
    metrics: Array<{
        id: Uuid;
    }>;
};

export type PointInTimeAsPeriodicDataResolverProps = BaseDataResolverProps & {
    datasetType: DatasetType.periodic;
    metrics: Array<{
        id: Uuid;
        aggregationFunction: MetricAggregationFunction;
    }>;
    granularity: TimeGranularity;
};

export type PointInTimeAsNonTimeSeriesDataResolverProps = BaseDataResolverProps & {
    datasetType: DatasetType.nonTimeSeries;
    metrics: Array<{
        id: Uuid;
        aggregationFunction: MetricAggregationFunction;
    }>;
    granularity: TimeGranularity;
};

export type PeriodicAsPeriodicDataResolverProps = BaseDataResolverProps & {
    datasetType: DatasetType.periodic;
    metrics: Array<{
        id: Uuid;
        // TODO: Confirm how to handle this???
        aggregationFunction: MetricAggregationFunction;
    }>;
    granularity: TimeGranularity;
};

export type PeriodicAsNonTimeSeriesDataResolverProps = BaseDataResolverProps & {
    datasetType: DatasetType.nonTimeSeries;
    metrics: Array<{
        id: Uuid;
        aggregationFunction: MetricAggregationFunction;
    }>;
    granularity: TimeGranularity;
};

export type NonTimeSeriesAsNonTimeSeriesDataResolverProps = BaseDataResolverProps & {
    datasetType: DatasetType.nonTimeSeries;
    metrics: Array<{
        id: Uuid;
        aggregationFunction: MetricAggregationFunction;
    }>;
};

export type DataResolverProps =
    | PointInTimeAsPointInTimeDataResolverProps
    | PointInTimeAsPeriodicDataResolverProps
    | PointInTimeAsNonTimeSeriesDataResolverProps
    | PeriodicAsPeriodicDataResolverProps
    | PeriodicAsNonTimeSeriesDataResolverProps
    | NonTimeSeriesAsNonTimeSeriesDataResolverProps;
